import { DocumentSendAudienceType } from '@/entities/DocumentSend';
import {
  buildProtheusPartyKey,
  resolveProtheusRecipientEmail,
  type ProtheusClient,
  type ProtheusRecipientType,
} from '@/entities/Protheus';
import { isValidEmailFormat } from '@/shared/lib';

type ProtheusRecipientOption = Pick<
  ProtheusClient,
  'code' | 'store' | 'name' | 'email' | 'distributionEmail'
>;

export const RECIPIENT_WITHOUT_EMAIL_MESSAGE =
  'Todos os destinatários selecionados devem possuir e-mail válido.';

const RECIPIENT_EMAIL_FIELD_PATTERN = /^recipients\.\d+\.email$/;

export function buildInvalidRecipientEmailsMessage(count: number): string {
  return count === 1
    ? '1 e-mail informado está em formato inválido. Verifique o destinatário e tente novamente.'
    : `${count} e-mails informados estão em formato inválido. Verifique os destinatários e tente novamente.`;
}

export interface ElectronicRecipientRow {
  protheusCode: string;
  protheusStore: string;
  name: string;
  email: string;
}

export interface BuiltElectronicRecipient {
  type: 'CLIENT' | 'SUPPLIER';
  protheusCode: string;
  protheusStore: string;
  name: string;
  email: string;
}

interface ValidateElectronicRecipientsParams {
  audienceType: DocumentSendAudienceType | '';
  clientRecipients: ElectronicRecipientRow[];
  supplierRecipients: ElectronicRecipientRow[];
  requireAudienceSelection?: boolean;
}

interface ValidatedElectronicRecipients {
  recipients: BuiltElectronicRecipient[];
  recipientError: string;
  invalidEmailCount: number;
}

const RECIPIENT_REQUIRED_MESSAGE = 'Ao menos um destinatário é obrigatório.';
const AUDIENCE_REQUIRED_MESSAGE = 'Selecione o tipo de destinatário.';

export function parseRecipientEmails(email: string): string[] {
  return [
    ...new Set(
      email
        .split(';')
        .map((part) => part.trim())
        .filter((part) => part !== '')
    ),
  ];
}

export function sanitizeRecipientEmail(email: string): string {
  return parseRecipientEmails(email).join('; ');
}

export function sanitizeElectronicRecipientRow(
  recipient: ElectronicRecipientRow
): ElectronicRecipientRow {
  return {
    ...recipient,
    email: parseRecipientEmails(recipient.email).join('; '),
  };
}

export function getElectronicRecipientRowKey(recipient: ElectronicRecipientRow): string {
  return buildProtheusPartyKey({ code: recipient.protheusCode, store: recipient.protheusStore });
}

export function toElectronicRecipientRow(
  recipient: ProtheusRecipientOption,
  recipientType: ProtheusRecipientType
): ElectronicRecipientRow {
  return {
    protheusCode: recipient.code,
    protheusStore: recipient.store,
    name: recipient.name,
    email: sanitizeRecipientEmail(resolveProtheusRecipientEmail(recipient, recipientType)),
  };
}

export function buildElectronicRecipients(
  audienceType: DocumentSendAudienceType | '',
  clientRecipients: ElectronicRecipientRow[],
  supplierRecipients: ElectronicRecipientRow[]
): BuiltElectronicRecipient[] | null {
  const selectedClientRecipients =
    audienceType === DocumentSendAudienceType.CLIENT ||
    audienceType === DocumentSendAudienceType.CLIENT_SUPPLIER
      ? clientRecipients
      : [];
  const selectedSupplierRecipients =
    audienceType === DocumentSendAudienceType.SUPPLIER ||
    audienceType === DocumentSendAudienceType.CLIENT_SUPPLIER
      ? supplierRecipients
      : [];

  const hasRecipientWithoutEmail = [
    ...selectedClientRecipients,
    ...selectedSupplierRecipients,
  ].some((recipient) => parseRecipientEmails(recipient.email).length === 0);

  if (hasRecipientWithoutEmail) {
    return null;
  }

  return [
    ...selectedClientRecipients.flatMap((recipient) =>
      parseRecipientEmails(recipient.email).map((email) => ({
        type: 'CLIENT' as const,
        protheusCode: recipient.protheusCode,
        protheusStore: recipient.protheusStore,
        name: recipient.name,
        email,
      }))
    ),
    ...selectedSupplierRecipients.flatMap((recipient) =>
      parseRecipientEmails(recipient.email).map((email) => ({
        type: 'SUPPLIER' as const,
        protheusCode: recipient.protheusCode,
        protheusStore: recipient.protheusStore,
        name: recipient.name,
        email,
      }))
    ),
  ];
}

export function isRecipientEmailInvalid(email: string): boolean {
  const emails = parseRecipientEmails(email);

  return emails.length === 0 || emails.some((item) => !isValidEmailFormat(item));
}

export function resolveInvalidRecipientEmailsMessage(
  fieldErrors: Record<string, string[]>
): string | null {
  const invalidEmailCount = Object.keys(fieldErrors).filter((field) =>
    RECIPIENT_EMAIL_FIELD_PATTERN.test(field)
  ).length;

  return invalidEmailCount === 0 ? null : buildInvalidRecipientEmailsMessage(invalidEmailCount);
}

export function validateElectronicRecipients({
  audienceType,
  clientRecipients,
  supplierRecipients,
  requireAudienceSelection = false,
}: ValidateElectronicRecipientsParams): ValidatedElectronicRecipients {
  if (requireAudienceSelection && audienceType === '') {
    return {
      recipients: [],
      recipientError: AUDIENCE_REQUIRED_MESSAGE,
      invalidEmailCount: 0,
    };
  }

  const builtRecipients = buildElectronicRecipients(
    audienceType,
    clientRecipients,
    supplierRecipients
  );

  if (builtRecipients === null) {
    return {
      recipients: [],
      recipientError: RECIPIENT_WITHOUT_EMAIL_MESSAGE,
      invalidEmailCount: 0,
    };
  }

  if (builtRecipients.length === 0) {
    return {
      recipients: [],
      recipientError: RECIPIENT_REQUIRED_MESSAGE,
      invalidEmailCount: 0,
    };
  }

  const invalidEmailCount = builtRecipients.filter(
    (recipient) => !isValidEmailFormat(recipient.email)
  ).length;

  if (invalidEmailCount > 0) {
    return {
      recipients: [],
      recipientError: buildInvalidRecipientEmailsMessage(invalidEmailCount),
      invalidEmailCount,
    };
  }

  return {
    recipients: builtRecipients,
    recipientError: '',
    invalidEmailCount: 0,
  };
}
